// Story 7.16 (ADO-1373), part B — ANCHORED, NON-MODAL popover (NEW primitive).
// Figma "Stock Card Popover (Desktop)" 3575:10456, shown in situ at 3581:10656.
//
// ── Why a new primitive rather than reusing one ──
// Neither existing surface can do this, and both were checked before writing it:
//
//   • `design-system/Modal` is a MODAL dialog — `aria-modal`, a Tab focus trap, a
//     body-scroll lock and a centred backdrop. Anchoring it to a trigger would mean
//     removing all four, which is not an adaptation but a different component.
//   • `app/Browse/SubstitutesPanel` is the repo's existing in-card overlay and is
//     the right shape in every respect except placement: it is positioned INSIDE
//     `.card`, which is `overflow: hidden` (ProductCard.module.css:11), so it
//     cannot leave the card. The approved design has this popover overlapping
//     neighbouring grid cells (3581:10656), so a card-clipped overlay cannot
//     render it.
//
// So this portals to `<body>` and positions itself from the trigger's rect.
//
// ── a11y (NFR24-26), and why it is NOT `aria-modal` ──
// `role="dialog"` labelled by its title, Escape closes, the close button takes
// focus on open, and the CALLER restores focus to the trigger on close (the
// `SubstitutesPanel` contract, mirrored). It deliberately does NOT trap Tab or
// lock scroll: it is an inline affordance on one card in a grid, not a
// page-blocking dialog, and trapping focus in it would strand a keyboard user who
// only wanted to read a figure and move on.
//
// ── Placement ──
// Below the trigger, left-aligned, flipping above when there is not room below and clamped into
// the viewport either way (`useAnchoredPlacement`, shared with the Info Panel since Story 11.10).
// Below ~768px CSS takes over entirely and presents it as a bottom sheet, matching the mobile
// Stock Card design (1361:7370), which is why the computed position rides CSS CUSTOM PROPERTIES
// rather than inline `top`/`left`: an inline longhand would beat the media query and pin the
// sheet mid-screen.

import { useEffect, useId, useRef } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon/Icon';
import { useAnchoredPlacement, useDismissAnchoredPanel } from '../hooks/useAnchoredPlacement';
import styles from './Popover.module.css';

export interface PopoverProps {
  readonly open: boolean;
  /** Dismiss. The CALLER returns focus to the trigger (see the a11y note). */
  readonly onClose: () => void;
  /** Names the dialog for assistive tech, and heads the panel. */
  readonly title: string;
  /**
   * The trigger the panel is positioned against. Also the one element an outside
   * click does NOT close on: the trigger owns the open/close toggle, and closing
   * here first would make its own click immediately reopen the panel.
   */
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly children: ReactNode;
}

export function Popover({ open, onClose, title, anchorRef, children }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const position = useAnchoredPlacement(open, anchorRef, panelRef);

  // Move focus into the panel on open (WAI-ARIA dialog pattern); the caller
  // restores focus to the trigger on close. `preventScroll` because the panel is
  // fixed and already in view — letting the browser scroll to it moves the page
  // under the user for no reason.
  useEffect(() => {
    if (open) closeRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Escape and an outside press close it, but not a press on its own trigger, which owns the
  // toggle (`useDismissAnchoredPanel`).
  useDismissAnchoredPanel(open, onClose, anchorRef, panelRef);

  if (!open) return null;

  // Custom properties, not inline `top`/`left`: the <768px bottom-sheet media
  // query has to be able to override the placement, and an inline longhand wins
  // against any stylesheet rule.
  const placement = {
    '--popover-top': `${position?.top ?? 0}px`,
    '--popover-left': `${position?.left ?? 0}px`,
  } as CSSProperties;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-labelledby={titleId}
      className={styles.popover}
      data-positioned={position !== null}
      style={placement}
    >
      <div className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
          ref={closeRef}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className={styles.body}>{children}</div>
    </div>,
    document.body,
  );
}
